import React from 'react';

import { useTranslation } from '@mint-lib/i18n';
import styled from '@mint-lib/styled';

import { Box, Button, Icon, PopoverItem, Typography } from '@myn/mui';

type Props = {
  setPreEditMode: (value: boolean) => void;
  handleWarningToggle?: () => void;
  handleDelete: () => void;
  handleCancel: () => void;
  isWarningOpen: boolean;
};

const PreEditModal: React.FC<Props> = ({
  handleWarningToggle,
  setPreEditMode,
  handleDelete,
  handleCancel,
  isWarningOpen,
}) => {
  const { t } = useTranslation('@myn/planning');

  const handleClose = () => {
    handleWarningToggle?.();
    handleCancel();
  };

  const handleDeleteClick = () => {
    handleClose();
    handleDelete();
  };

  return (
    <Box>
      {isWarningOpen ? (
        <WarningWrapper>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: (theme) => theme.spacing(1),
            }}
          >
            <Icon component="WarningAltFilled" color="error" />
            <Typography variant="h4">{t('Delete this column?')}</Typography>
          </Box>
          <Typography variant="bodyShort01">
            {t('The action is irreversible and all data will be lost.')}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: (theme) => theme.spacing(1),
            }}
          >
            <Button variant="text" size="large" onClick={handleClose}>
              {t('Cancel')}
            </Button>
            <Button
              variant="contained"
              size="large"
              color="error"
              onClick={handleDeleteClick}
            >
              {t('Delete')}
            </Button>
          </Box>
        </WarningWrapper>
      ) : (
        <>
          <PopoverItem
            text={t('Edit')}
            endAdornment={<Icon component="Edit" />}
            size="large"
            onClick={() => setPreEditMode(false)}
          />
          <PopoverItem
            size="large"
            sx={{ color: ({ palette }) => palette.error.main }}
            endAdornment={<Icon component="Delete" />}
            text={t('Delete')}
            onClick={handleWarningToggle}
          />
        </>
      )}
    </Box>
  );
};

const WarningWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(4)};
  overflow: hidden;
  width: 320px;
  padding: ${({ theme }) => theme.spacing(2)};
`;

export default PreEditModal;
